import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, Camera, PencilSimple, Trash, X, Paperclip, FilePlus, QrCode } from "@phosphor-icons/react";
import OcrDialog from "@/components/OcrDialog";
import { useAuth } from "@/context/AuthContext";
import { compressImageTo1MB, fmtKB } from "@/lib/imageCompress";
import { fmtYmd, fmtYmdTime } from "@/lib/dateFormat";
import DatePickerYmd from "@/components/DatePickerYmd";

const STATUS = [
    { v: "", l: "全て" },
    { v: "active", l: "稼働中" },
    { v: "draft", l: "仮登録" },
    { v: "in_maintenance", l: "整備中" },
    { v: "retired", l: "廃車" },
];

function StatusBadge({ status }) {
    const map = {
        active: { cls: "ok", l: "稼働中" },
        draft: { cls: "info", l: "仮登録" },
        in_maintenance: { cls: "warn", l: "整備中" },
        retired: { cls: "muted", l: "廃車" },
    };
    const s = map[status] || { cls: "muted", l: status };
    return <span className={`prc-badge ${s.cls}`}>{s.l}</span>;
}

export default function Vehicles() {
    const { can, features } = useAuth();
    const canManage = can("vehicles.manage") || can("vehicles.create");
    const [list, setList] = useState([]);
    // Attachments popup: { vehicleId, plate, attachments, busy }
    const [attachPopup, setAttachPopup] = useState(null);
    const [status, setStatus] = useState("");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [ocrOpen, setOcrOpen] = useState(false);
    const [ocrInFormOpen, setOcrInFormOpen] = useState(false);
    const [newOpen, setNewOpen] = useState(false);
    const [editId, setEditId] = useState(null);  // string | null
    const [fromOcr, setFromOcr] = useState(false);
    const [ocrConfidence, setOcrConfidence] = useState({});  // { field: 'high'|'medium'|'low' }
    const [ocrSources, setOcrSources] = useState({});  // { field: 'qr'|'easyocr'|'tesseract' }
    const [editTab, setEditTab] = useState("form");  // 'form' | 'mileage' | 'lifeline' | 'history' (only meaningful when editing)
    const [form, setForm] = useState({ plate_number: "", make: "", model: "", year: "", inspection_expiry: "", department: "", status: "active" });

    const load = async () => {
        setLoading(true);
        const { data } = await api.get("/vehicles", { params: { status: status || undefined, q: q || undefined } });
        setList(data);
        setLoading(false);
    };

    useEffect(() => {
        load();
    }, [status]); // eslint-disable-line

    const emptyForm = {
        // 1. 基本情報
        plate_number: "", chassis_number: "", issue_date: "", first_inspection_date: "", inspection_expiry: "",
        // 2. 使用者・所有者情報
        user_name: "", user_address: "", owner_name: "", owner_address: "", usage_base_address: "",
        // 3. 車両詳細情報
        make: "", model: "", engine_model: "", vehicle_type: "", usage: "", private_business: "", body_shape: "",
        seating_capacity: "", max_load_kg: "", vehicle_weight_kg: "", gross_weight_kg: "",
        length_cm: "", width_cm: "", height_cm: "", front_axle_weight_kg: "", rear_axle_weight_kg: "",
        displacement_l: "", fuel_type: "", type_designation_no: "", classification_no: "",
        // 4. 備考 + 管理
        notes: "", department: "", status: "active",
        // 5. 車検証画像 / 自賠責保険
        inspection_cert_image_url: "", jibaiseki_company: "", jibaiseki_policy_no: "",
        jibaiseki_start_date: "", jibaiseki_end_date: "", jibaiseki_premium_jpy: "",
        jibaiseki_image_url: "",
    };

    const openNew = () => {
        setEditId(null);
        setFromOcr(false);
        setOcrConfidence({});
        setOcrSources({});
        setForm(emptyForm);
        setNewOpen(true);
    };

    /** Open a new tab with every visible vehicle's QR code rendered on
     *  A5-sized cards, ready to be sent to the printer.
     *
     *  QR SVGs are fetched in the parent window (uses axios with cookies /
     *  bearer token) so the print tab does not need to make any cross-origin
     *  requests — this avoids CORS failures that would otherwise leave the
     *  page blank.
     */
    const openBulkQr = async () => {
        const vs = list;
        if (!vs || vs.length === 0) {
            toast.error("印刷対象の車両がありません");
            return;
        }
        const t = toast.loading(`${vs.length} 台の QR コードを準備中...`);
        // Fetch all QR SVGs in the parent window first — cookies + bearer flow
        // through the existing axios instance, no CORS issues.
        const svgs = await Promise.all(
            vs.map(async (v) => {
                try {
                    const res = await api.get(`/vehicles/${v.id}/qr-code.svg`, { responseType: "text" });
                    return { v, svg: res.data };
                } catch (e) {
                    return { v, svg: "" };
                }
            })
        );
        toast.dismiss(t);
        const okCount = svgs.filter((s) => s.svg).length;
        if (okCount === 0) {
            toast.error("QR コードを取得できませんでした");
            return;
        }
        if (okCount < vs.length) {
            toast.warning(`${vs.length - okCount} 台のQR取得に失敗しましたが、残りを印刷します`);
        }
        const w = window.open("", "_blank", "width=800,height=1000");
        if (!w) {
            toast.error("ポップアップがブロックされました。ブラウザの設定でこのサイトのポップアップを許可してください");
            return;
        }
        const escapeHtml = (s) => String(s || "").replace(/[&<>"']/g, (c) => ({
            "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
        }[c]));
        const rows = svgs.map(({ v, svg }) => `
            <div class="qr-card">
                <div class="qr-plate">${escapeHtml(v.plate_number)}</div>
                <div class="qr-model">${escapeHtml((v.make || "") + " " + (v.model || ""))}</div>
                <div class="qr-slot">${svg || '<span class="loading">取得失敗</span>'}</div>
                <div class="qr-foot">PRC CMC · QRスキャンで車両選択</div>
            </div>`).join("");
        w.document.write(`<!DOCTYPE html><html lang="ja"><head><meta charset="utf-8" />
<title>車両QR 一括印刷 (${vs.length}台)</title>
<style>
  body{font-family:sans-serif;background:#f5f5f5;margin:0;padding:12px}
  .wrap{display:grid;grid-template-columns:repeat(2,1fr);gap:12px}
  .qr-card{background:#fff;border:1px solid #ddd;border-radius:6px;padding:14px;
    display:flex;flex-direction:column;align-items:center;page-break-inside:avoid;break-inside:avoid;
    min-height:340px;justify-content:center}
  .qr-plate{font-size:24px;font-weight:900;letter-spacing:1.5px;margin-bottom:6px;text-align:center;color:#000}
  .qr-model{font-size:11px;color:#666;margin-bottom:10px;text-align:center}
  .qr-slot{width:200px;height:200px;padding:6px;background:#fff}
  .qr-slot svg,.qr-slot img{width:100%;height:100%;display:block}
  .qr-slot .loading{color:#c00;font-size:11px;text-align:center;display:block;padding-top:80px}
  .qr-foot{margin-top:10px;font-size:9px;color:#999;letter-spacing:1px;text-transform:uppercase}
  .no-print{position:fixed;top:12px;right:12px;background:#000;color:#fff;padding:8px 14px;
    border-radius:4px;cursor:pointer;border:0;font-size:13px}
  @media print{.no-print{display:none}body{background:#fff;padding:0}.qr-card{border:none}}
</style></head><body>
<button class="no-print" onclick="window.print()">印刷</button>
<div class="wrap">${rows}</div>
<script>window.onload=function(){setTimeout(function(){window.print()},600)};<\/script>
</body></html>`);
        w.document.close();
    };

    // OCR extraction callback: open 新規登録 form pre-filled with extracted fields
    const handleOcrExtracted = (extracted, _dataUrl, confidence = {}, sources = {}) => {
        setOcrOpen(false);
        const merged = { ...emptyForm };
        for (const k of Object.keys(emptyForm)) {
            const v = extracted[k];
            if (v != null && v !== "") {
                merged[k] = typeof v === "number" ? String(v) : String(v);
            }
        }
        setEditId(null);
        setFromOcr(true);
        setOcrConfidence(confidence);
        setOcrSources(sources);
        setForm(merged);
        setNewOpen(true);
    };

    // OCR extraction while already editing the form — merge extracted into current form
    const handleOcrExtractedInForm = (extracted, _dataUrl, confidence = {}, sources = {}) => {
        setOcrInFormOpen(false);
        setForm((prev) => {
            const merged = { ...prev };
            for (const k of Object.keys(emptyForm)) {
                const v = extracted[k];
                if (v != null && v !== "") {
                    merged[k] = typeof v === "number" ? String(v) : String(v);
                }
            }
            return merged;
        });
        setOcrConfidence((prev) => ({ ...prev, ...confidence }));
        setOcrSources((prev) => ({ ...prev, ...sources }));
        setFromOcr(true);
    };

    const openEdit = (v) => {
        setEditId(v.id);
        const merged = { ...emptyForm };
        for (const k of Object.keys(emptyForm)) {
            if (v[k] != null) merged[k] = typeof v[k] === "number" ? String(v[k]) : v[k];
        }
        setOcrConfidence({});
        setOcrSources({});
        setFromOcr(false);
        setEditTab("form");
        setForm(merged);
        setNewOpen(true);
    };

    // Duplicate-resolution dialog state. When backend returns 409 on POST,
    // we surface { existing_vehicle, payload } and let the user pick:
    //   "更新" → PATCH the existing vehicle with current form data
    //   "キャンセル" → keep form open
    const [dupConfirm, setDupConfirm] = useState(null);

    const submitNew = async (e) => {
        e.preventDefault();
        try {
            // Convert numeric strings to numbers where appropriate
            const intKeys = ["seating_capacity","max_load_kg","vehicle_weight_kg","gross_weight_kg","length_cm","width_cm","height_cm","front_axle_weight_kg","rear_axle_weight_kg","jibaiseki_premium_jpy"];
            const floatKeys = ["displacement_l"];
            const payload = { ...form };
            for (const k of intKeys) payload[k] = payload[k] === "" || payload[k] == null ? null : parseInt(payload[k]);
            for (const k of floatKeys) payload[k] = payload[k] === "" || payload[k] == null ? null : parseFloat(payload[k]);
            // Strip empty strings so PATCH doesn't override to ""
            for (const k of Object.keys(payload)) {
                if (payload[k] === "") payload[k] = null;
            }
            if (editId) {
                await api.patch(`/vehicles/${editId}`, payload);
                toast.success("車両を更新しました");
            } else {
                await api.post("/vehicles", payload);
                toast.success("車両を登録しました");
            }
            setNewOpen(false);
            setEditId(null);
            setFromOcr(false);
            setOcrConfidence({});
            setOcrSources({});
            setForm(emptyForm);
            await load();
        } catch (e) {
            const detail = e.response?.data?.detail;
            // Duplicate vehicle → open confirmation dialog
            if (e.response?.status === 409 && detail?.code === "duplicate_vehicle" && detail.existing_vehicle) {
                // Re-build payload (same as success path) so the merge action
                // can reuse it without re-parsing the form
                const intKeys = ["seating_capacity","max_load_kg","vehicle_weight_kg","gross_weight_kg","length_cm","width_cm","height_cm","front_axle_weight_kg","rear_axle_weight_kg","jibaiseki_premium_jpy"];
                const floatKeys = ["displacement_l"];
                const payload = { ...form };
                for (const k of intKeys) payload[k] = payload[k] === "" || payload[k] == null ? null : parseInt(payload[k]);
                for (const k of floatKeys) payload[k] = payload[k] === "" || payload[k] == null ? null : parseFloat(payload[k]);
                for (const k of Object.keys(payload)) {
                    if (payload[k] === "") payload[k] = null;
                }
                setDupConfirm({
                    existing: detail.existing_vehicle,
                    matchField: detail.match_field,
                    payload,
                });
                return;
            }
            toast.error(formatApiError(detail));
        }
    };

    // Confirm "update existing vehicle" from duplicate dialog.
    // `decisions` is a map { field: 'overwrite'|'keep' } — only fields marked
    // 'overwrite' are sent to PATCH. Empty input is treated as 'keep' implicitly.
    const confirmUpdateExisting = async (decisions) => {
        if (!dupConfirm) return;
        const { existing, payload } = dupConfirm;
        try {
            const patchBody = {};
            for (const [k, v] of Object.entries(payload)) {
                if (v == null || v === "") continue;
                if (decisions[k] === "overwrite") patchBody[k] = v;
            }
            if (Object.keys(patchBody).length === 0) {
                toast.info("上書きする項目が選択されていません");
                return;
            }
            // Tag history source: 'ocr_diff' when the data originated from OCR
            // so the change appears with the sky-blue OCR pill in history view.
            const src = fromOcr ? "ocr_diff" : "manual";
            await api.patch(`/vehicles/${existing.id}?source=${src}`, patchBody);
            toast.success(`既存車両「${existing.plate_number}」を ${Object.keys(patchBody).length} 項目更新しました`);
            setDupConfirm(null);
            setNewOpen(false);
            setEditId(null);
            setFromOcr(false);
            setOcrConfidence({});
            setOcrSources({});
            setForm(emptyForm);
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "更新に失敗しました");
        }
    };

    const openAttachments = async (v) => {
        setAttachPopup({ vehicleId: v.id, plate: v.plate_number, attachments: [], busy: true });
        try {
            const { data } = await api.get(`/vehicles/${v.id}/attachments`);
            setAttachPopup({ vehicleId: v.id, plate: data.plate_number, attachments: data.attachments || [], busy: false });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "添付の取得に失敗しました");
            setAttachPopup(null);
        }
    };

    const uploadAttachmentForVehicle = async (vehicleId, file) => {
        // Attachment limit is 2MB (user request). compressImageTo1MB has a
        // 1MB ceiling so we use a dedicated multi-step compressor here.
        const TARGET = 2 * 1024 * 1024;
        const STEPS = [
            { maxEdge: 2400, quality: 0.88 },
            { maxEdge: 2000, quality: 0.85 },
            { maxEdge: 1600, quality: 0.8 },
            { maxEdge: 1280, quality: 0.72 },
            { maxEdge: 960, quality: 0.62 },
        ];
        const readAs = (f) => new Promise((res, rej) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.onerror = () => rej(new Error("file read failed"));
            r.readAsDataURL(f);
        });
        const resizeOnce = async (f, maxEdge, quality) => {
            const du = await readAs(f);
            return new Promise((res, rej) => {
                const img = new Image();
                img.onload = () => {
                    const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
                    const w = Math.max(1, Math.round(img.width * scale));
                    const h = Math.max(1, Math.round(img.height * scale));
                    const c = document.createElement("canvas");
                    c.width = w; c.height = h;
                    c.getContext("2d").drawImage(img, 0, 0, w, h);
                    res(c.toDataURL("image/jpeg", quality));
                };
                img.onerror = () => rej(new Error("decode failed"));
                img.src = du;
            });
        };
        try {
            if (!file.type?.startsWith("image/")) {
                toast.error("画像ファイルを選択してください");
                return;
            }
            if (file.size > 25 * 1024 * 1024) {
                toast.error("画像は25MB以下にしてください");
                return;
            }
            let dataUrl = "";
            let bytes = Infinity;
            for (const s of STEPS) {
                dataUrl = await resizeOnce(file, s.maxEdge, s.quality);
                bytes = Math.ceil((dataUrl.length - dataUrl.indexOf(",") - 1) * 3 / 4);
                if (bytes <= TARGET) break;
            }
            if (bytes > TARGET) {
                toast.error("画像を2MB以下に圧縮できませんでした");
                return;
            }
            const { data } = await api.post(`/vehicles/${vehicleId}/attachments`, {
                kind: "other",
                name: file.name,
                data_url: dataUrl,
            });
            toast.success(`添付を追加しました (${(bytes / 1024).toFixed(0)} KB)`);
            if (attachPopup && attachPopup.vehicleId === vehicleId) {
                setAttachPopup({ ...attachPopup, attachments: [...attachPopup.attachments, data] });
            }
            load();
        } catch (err) {
            toast.error(err.message || formatApiError(err.response?.data?.detail) || "添付失敗");
        }
    };

    const removeAttachment = async (vehicleId, attachId) => {
        if (!window.confirm("この添付を削除しますか？")) return;
        try {
            await api.delete(`/vehicles/${vehicleId}/attachments/${attachId}`);
            if (attachPopup && attachPopup.vehicleId === vehicleId) {
                setAttachPopup({ ...attachPopup, attachments: attachPopup.attachments.filter(a => a.id !== attachId) });
            }
            load();
            toast.success("削除しました");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "削除失敗");
        }
    };

    const deleteVehicle = async (v) => {
        if (!window.confirm(`車両「${v.plate_number}」を削除しますか？\nこの操作は取り消せません。`)) return;
        try {
            await api.delete(`/vehicles/${v.id}`);
            toast.success("車両を削除しました");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-5" data-testid="vehicles-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="prc-label-overline">車両台帳</p>
                    <h1 className="text-[1.685rem] font-bold tracking-tight">車両一覧</h1>
                </div>
                <div className="flex gap-2">
                    {can("vehicles.ocr") && (
                        <button onClick={() => setOcrOpen(true)} className="prc-btn-ghost inline-flex items-center gap-2" data-testid="vehicles-ocr-btn">
                            <Camera size={16} />車検証OCR登録
                        </button>
                    )}
                    <button onClick={openBulkQr} className="prc-btn-ghost inline-flex items-center gap-2" data-testid="vehicles-bulk-qr-btn">
                        <QrCode size={16} />QR一括印刷
                    </button>
                    {can("vehicles.create") && (
                        <button onClick={openNew} className="prc-btn-primary inline-flex items-center gap-2" data-testid="vehicles-new-btn">
                            <Plus size={16} />車両を新規登録
                        </button>
                    )}
                </div>
            </div>

            <div className="prc-card p-4 flex flex-wrap gap-3 items-center">
                <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                    <MagnifyingGlass size={16} className="text-[var(--prc-muted)]" />
                    <input
                        placeholder="登録番号・メーカー・車種で検索"
                        className="prc-input flex-1"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        data-testid="vehicles-search-input"
                    />
                </div>
                <div className="flex gap-1" data-testid="vehicles-status-filter">
                    {STATUS.map((s) => (
                        <button
                            key={s.v}
                            onClick={() => setStatus(s.v)}
                            className={`px-3 py-1.5 text-xs font-semibold border rounded-sm transition ${status === s.v ? "bg-[var(--prc-green)] text-black border-[var(--prc-green)]" : "bg-[var(--prc-surface-2)] border-[var(--prc-border-strong)] text-[var(--prc-muted)]"}`}
                            data-testid={`filter-${s.v || "all"}`}
                        >
                            {s.l}
                        </button>
                    ))}
                </div>
            </div>

            <div className="prc-card overflow-x-auto">
                {loading ? (
                    <p className="p-5 text-sm text-[var(--prc-muted)]" data-testid="vehicles-loading">読込中…</p>
                ) : list.length === 0 ? (
                    <p className="p-8 text-sm text-[var(--prc-muted)] text-center" data-testid="vehicles-empty">車両が登録されていません。右上から登録してください。</p>
                ) : (
                    <table className="prc-table" data-testid="vehicles-table">
                    <thead>
                        <tr>
                            <th>車両番号</th>
                            <th>車名 / 型式</th>
                            <th>使用者</th>
                            <th>有効期間満了日</th>
                            <th>ステータス</th>
                            <th className="text-center">添付</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((v) => (
                            <tr key={v.id} data-testid={`vehicle-row-${v.id}`}>
                                <td className="font-mono font-semibold">{v.plate_number}</td>
                                <td>{v.make || "-"} / {v.model || "-"}</td>
                                <td>{v.user_name || v.department || "-"}</td>
                                <td className="num">{v.inspection_expiry ? fmtYmd(v.inspection_expiry, v.inspection_expiry) : "-"}</td>
                                <td><StatusBadge status={v.status} /></td>
                                <td className="text-center">
                                    {(() => {
                                        const total = (v.attachments_count || 0) + (v.has_cert_image ? 1 : 0) + (v.has_jci_image ? 1 : 0);
                                        return (
                                            <button
                                                type="button"
                                                onClick={() => openAttachments(v)}
                                                className="prc-btn-ghost !px-2 !py-1 inline-flex items-center gap-1 text-xs"
                                                data-testid={`vehicle-attach-${v.id}`}
                                                title="添付書類を見る"
                                            >
                                                <Paperclip size={16} />
                                                {total > 0 && <span className="text-[var(--prc-green)] font-mono">{total}</span>}
                                            </button>
                                        );
                                    })()}
                                </td>
                                <td className="text-right whitespace-nowrap">
                                    <div className="inline-flex items-center gap-1.5">
                                        <Link to={`/vehicles/${v.id}`} className="text-xs font-semibold hover:underline text-[var(--prc-green)]" data-testid={`vehicle-view-${v.id}`}>詳細</Link>
                                        {canManage && (
                                            <>
                                                <button
                                                    onClick={() => openEdit(v)}
                                                    className="prc-btn-ghost !px-2 !py-1 inline-flex items-center gap-1 text-xs"
                                                    data-testid={`vehicle-edit-${v.id}`}
                                                    title="編集"
                                                >
                                                    <PencilSimple size={16} /> 編集
                                                </button>
                                                <button
                                                    onClick={() => deleteVehicle(v)}
                                                    className="prc-btn-ghost !px-2 !py-1 inline-flex items-center gap-1 text-xs text-[var(--prc-red)] hover:!border-[var(--prc-red)]"
                                                    data-testid={`vehicle-delete-${v.id}`}
                                                    title="削除"
                                                >
                                                    <Trash size={16} /> 削除
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                )}
            </div>

            {/* New/Edit Vehicle Dialog — 車検証 記録事項に準拠 */}
            {newOpen && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="new-vehicle-dialog">
                    <form onSubmit={submitNew} className="prc-card p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
                        <button
                            type="button"
                            onClick={() => { setNewOpen(false); setEditId(null); setForm(emptyForm); setEditTab("form"); }}
                            className="absolute top-3 right-3 prc-btn-ghost !p-0 h-8 w-8 inline-flex items-center justify-center"
                            aria-label="閉じる"
                            title="閉じる"
                            data-testid="vehicle-dialog-close"
                        ><X size={16} /></button>
                        <div className="flex items-start justify-end gap-3 mb-3 flex-wrap pr-10">
                            {!editId && can("vehicles.ocr") && (
                                <button
                                    type="button"
                                    onClick={() => setOcrInFormOpen(true)}
                                    className="prc-btn-ghost inline-flex items-center gap-2 whitespace-nowrap"
                                    data-testid="new-vehicle-ocr-btn"
                                    title="車検証の写真をアップロードして各項目を自動入力"
                                >
                                    <Camera size={16} />車検証OCRで自動入力
                                </button>
                            )}
                        </div>
                        {editId && (
                            <div className="mb-4 flex items-center gap-1 border-b border-[var(--prc-border)]" data-testid="edit-tabs">
                                <button
                                    type="button"
                                    onClick={() => setEditTab("form")}
                                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border-b-2 -mb-px transition ${editTab === "form" ? "border-[var(--prc-green)] text-[var(--prc-green)]" : "border-transparent text-[var(--prc-muted)] hover:text-white"}`}
                                    data-testid="edit-tab-form"
                                >// 編集</button>
                                <button
                                    type="button"
                                    onClick={() => setEditTab("mileage")}
                                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border-b-2 -mb-px transition ${editTab === "mileage" ? "border-[var(--prc-green)] text-[var(--prc-green)]" : "border-transparent text-[var(--prc-muted)] hover:text-white"}`}
                                    data-testid="edit-tab-mileage"
                                >// 整備計画</button>
                                <button
                                    type="button"
                                    onClick={() => setEditTab("lifeline")}
                                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border-b-2 -mb-px transition ${editTab === "lifeline" ? "border-[var(--prc-green)] text-[var(--prc-green)]" : "border-transparent text-[var(--prc-muted)] hover:text-white"}`}
                                    data-testid="edit-tab-lifeline"
                                >// ライフライン</button>
                                <button
                                    type="button"
                                    onClick={() => setEditTab("history")}
                                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border-b-2 -mb-px transition ${editTab === "history" ? "border-[var(--prc-green)] text-[var(--prc-green)]" : "border-transparent text-[var(--prc-muted)] hover:text-white"}`}
                                    data-testid="edit-tab-history"
                                >// 変更履歴</button>
                            </div>
                        )}
                        {editId && editTab === "mileage" ? (
                            <VehicleMileageForecastView vehicleId={editId} onClose={() => { setNewOpen(false); setEditId(null); setEditTab("form"); }} />
                        ) : editId && editTab === "lifeline" ? (
                            <VehicleLifelineView vehicleId={editId} onClose={() => { setNewOpen(false); setEditId(null); setEditTab("form"); }} />
                        ) : editId && editTab === "history" ? (
                            <VehicleHistoryView vehicleId={editId} onClose={() => { setNewOpen(false); setEditId(null); setEditTab("form"); }} />
                        ) : (
                        <>
                        {/* OCR summary + review banners removed per user request — the OCR
                            badges on each field already indicate source & confidence. */}

                        <Section title="1. 基本情報">
                            <Field label="車両番号 *" cols={2} field="plate_number" sources={ocrSources} confidence={ocrConfidence}><input required className="prc-input" value={form.plate_number} onChange={(e) => setForm({ ...form, plate_number: e.target.value })} data-testid="new-plate-input" /></Field>
                            <Field label="車台番号" cols={2} field="chassis_number" sources={ocrSources} confidence={ocrConfidence}><input className="prc-input" value={form.chassis_number} onChange={(e) => setForm({ ...form, chassis_number: e.target.value })} /></Field>
                            <Field label="交付年月日" field="issue_date" sources={ocrSources} confidence={ocrConfidence}><DatePickerYmd value={form.issue_date} onChange={(val) => setForm({ ...form, issue_date: val })} /></Field>
                            <Field label="初度検査年月" field="first_inspection_date" sources={ocrSources} confidence={ocrConfidence}><input type="month" className="prc-input" value={form.first_inspection_date} onChange={(e) => setForm({ ...form, first_inspection_date: e.target.value })} /></Field>
                            <Field label="有効期間の満了する日" field="inspection_expiry" sources={ocrSources} confidence={ocrConfidence}><DatePickerYmd value={form.inspection_expiry} onChange={(val) => setForm({ ...form, inspection_expiry: val })} testid="new-expiry-input" /></Field>
                        </Section>

                        <Section title="2. 使用者・所有者情報">
                            <Field label="使用者の氏名又は名称" cols={2} field="user_name" sources={ocrSources} confidence={ocrConfidence}><input className="prc-input" value={form.user_name} onChange={(e) => setForm({ ...form, user_name: e.target.value })} /></Field>
                            <Field label="使用者の住所" cols={2} field="user_address" sources={ocrSources} confidence={ocrConfidence}><input className="prc-input" value={form.user_address} onChange={(e) => setForm({ ...form, user_address: e.target.value })} /></Field>
                            <Field label="所有者の氏名又は名称" cols={2} field="owner_name" sources={ocrSources} confidence={ocrConfidence}><input className="prc-input" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} /></Field>
                            <Field label="所有者の住所" cols={2} field="owner_address" sources={ocrSources} confidence={ocrConfidence}><input className="prc-input" value={form.owner_address} onChange={(e) => setForm({ ...form, owner_address: e.target.value })} /></Field>
                            <Field label="使用の本拠の位置" cols={2} field="usage_base_address" sources={ocrSources} confidence={ocrConfidence}><input className="prc-input" value={form.usage_base_address} onChange={(e) => setForm({ ...form, usage_base_address: e.target.value })} /></Field>
                        </Section>

                        <Section title="3. 車両詳細情報">
                            <Field label="車名" field="make" sources={ocrSources} confidence={ocrConfidence}><input className="prc-input" value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} data-testid="new-make-input" /></Field>
                            <Field label="型式" field="model" sources={ocrSources} confidence={ocrConfidence}><input className="prc-input" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="new-model-input" /></Field>
                            <Field label="原動機の型式" field="engine_model" sources={ocrSources} confidence={ocrConfidence}><input className="prc-input" value={form.engine_model} onChange={(e) => setForm({ ...form, engine_model: e.target.value })} /></Field>
                            <Field label="自動車の種別" field="vehicle_type" sources={ocrSources} confidence={ocrConfidence}><input className="prc-input" value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} /></Field>
                            <Field label="用途" field="usage" sources={ocrSources} confidence={ocrConfidence}><input className="prc-input" value={form.usage} onChange={(e) => setForm({ ...form, usage: e.target.value })} /></Field>
                            <Field label="自家用・事業用の別" field="private_business" sources={ocrSources} confidence={ocrConfidence}><input className="prc-input" value={form.private_business} onChange={(e) => setForm({ ...form, private_business: e.target.value })} /></Field>
                            <Field label="車体の形状" field="body_shape" sources={ocrSources} confidence={ocrConfidence}><input className="prc-input" value={form.body_shape} onChange={(e) => setForm({ ...form, body_shape: e.target.value })} /></Field>
                            <Field label="乗車定員 (人)" field="seating_capacity" sources={ocrSources} confidence={ocrConfidence}><input type="number" className="prc-input" value={form.seating_capacity} onChange={(e) => setForm({ ...form, seating_capacity: e.target.value })} /></Field>
                            <Field label="最大積載量 (kg)" field="max_load_kg" sources={ocrSources} confidence={ocrConfidence}><input type="number" className="prc-input" value={form.max_load_kg} onChange={(e) => setForm({ ...form, max_load_kg: e.target.value })} /></Field>
                            <Field label="車両重量 (kg)" field="vehicle_weight_kg" sources={ocrSources} confidence={ocrConfidence}><input type="number" className="prc-input" value={form.vehicle_weight_kg} onChange={(e) => setForm({ ...form, vehicle_weight_kg: e.target.value })} /></Field>
                            <Field label="車両総重量 (kg)" field="gross_weight_kg" sources={ocrSources} confidence={ocrConfidence}><input type="number" className="prc-input" value={form.gross_weight_kg} onChange={(e) => setForm({ ...form, gross_weight_kg: e.target.value })} /></Field>
                            <Field label="長さ (cm)" field="length_cm" sources={ocrSources} confidence={ocrConfidence}><input type="number" className="prc-input" value={form.length_cm} onChange={(e) => setForm({ ...form, length_cm: e.target.value })} /></Field>
                            <Field label="幅 (cm)" field="width_cm" sources={ocrSources} confidence={ocrConfidence}><input type="number" className="prc-input" value={form.width_cm} onChange={(e) => setForm({ ...form, width_cm: e.target.value })} /></Field>
                            <Field label="高さ (cm)" field="height_cm" sources={ocrSources} confidence={ocrConfidence}><input type="number" className="prc-input" value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} /></Field>
                            <Field label="前軸重 (kg)" field="front_axle_weight_kg" sources={ocrSources} confidence={ocrConfidence}><input type="number" className="prc-input" value={form.front_axle_weight_kg} onChange={(e) => setForm({ ...form, front_axle_weight_kg: e.target.value })} /></Field>
                            <Field label="後軸重 (kg)" field="rear_axle_weight_kg" sources={ocrSources} confidence={ocrConfidence}><input type="number" className="prc-input" value={form.rear_axle_weight_kg} onChange={(e) => setForm({ ...form, rear_axle_weight_kg: e.target.value })} /></Field>
                            <Field label="総排気量 又は 定格出力 (L)" field="displacement_l" sources={ocrSources} confidence={ocrConfidence}><input type="number" step="0.01" className="prc-input" value={form.displacement_l} onChange={(e) => setForm({ ...form, displacement_l: e.target.value })} /></Field>
                            <Field label="燃料の種類" field="fuel_type" sources={ocrSources} confidence={ocrConfidence}><input className="prc-input" value={form.fuel_type} onChange={(e) => setForm({ ...form, fuel_type: e.target.value })} /></Field>
                            <Field label="型式指定番号" field="type_designation_no" sources={ocrSources} confidence={ocrConfidence}><input className="prc-input" value={form.type_designation_no} onChange={(e) => setForm({ ...form, type_designation_no: e.target.value })} /></Field>
                            <Field label="類別区分番号" field="classification_no" sources={ocrSources} confidence={ocrConfidence}><input className="prc-input" value={form.classification_no} onChange={(e) => setForm({ ...form, classification_no: e.target.value })} /></Field>
                        </Section>

                        <Section title="5. 自賠責保険">
                            <Field label="自賠責 保険会社"><input className="prc-input" value={form.jibaiseki_company || ""} onChange={(e) => setForm({ ...form, jibaiseki_company: e.target.value })} /></Field>
                            <Field label="自賠責 証券番号"><input className="prc-input" value={form.jibaiseki_policy_no || ""} onChange={(e) => setForm({ ...form, jibaiseki_policy_no: e.target.value })} /></Field>
                            <Field label="自賠責 保険始期"><DatePickerYmd value={form.jibaiseki_start_date || ""} onChange={(val) => setForm({ ...form, jibaiseki_start_date: val })} /></Field>
                            <Field label="自賠責 保険終期"><DatePickerYmd value={form.jibaiseki_end_date || ""} onChange={(val) => setForm({ ...form, jibaiseki_end_date: val })} /></Field>
                            <Field label="自賠責 保険料 (¥)"><input type="number" className="prc-input" value={form.jibaiseki_premium_jpy || ""} onChange={(e) => setForm({ ...form, jibaiseki_premium_jpy: e.target.value })} /></Field>
                        </Section>

                        <Section title="6. 備考 / 管理情報">
                            <Field label="備考" cols={2} field="notes" sources={ocrSources} confidence={ocrConfidence}><textarea className="prc-input min-h-[80px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
                            <Field label="所属部署 (社内)"><input className="prc-input" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="new-dept-input" /></Field>
                            <Field label="ステータス">
                                <select className="prc-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="new-status-select">
                                    <option value="active">稼働中</option>
                                    <option value="draft">仮登録</option>
                                    <option value="in_maintenance">整備中</option>
                                    <option value="retired">廃車</option>
                                </select>
                            </Field>
                        </Section>

                        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-[var(--prc-border)]">
                            <button type="button" onClick={() => { setNewOpen(false); setEditId(null); setFromOcr(false); setOcrConfidence({}); setOcrSources({}); setEditTab("form"); }} className="prc-btn-ghost" data-testid="new-cancel-btn">キャンセル</button>
                            <button type="submit" className="prc-btn-primary" data-testid="new-submit-btn">{editId ? "更新" : "登録"}</button>
                        </div>
                        </>
                        )}
                    </form>
                </div>
            )}

            <OcrDialog open={ocrOpen} onClose={() => setOcrOpen(false)} onExtracted={handleOcrExtracted} />
            <OcrDialog open={ocrInFormOpen} onClose={() => setOcrInFormOpen(false)} onExtracted={handleOcrExtractedInForm} />

            {attachPopup && (
                <AttachmentsPopup
                    popup={attachPopup}
                    canEdit={can && can("vehicles.update")}
                    onClose={() => setAttachPopup(null)}
                    onUpload={(file) => uploadAttachmentForVehicle(attachPopup.vehicleId, file)}
                    onRemove={(id) => removeAttachment(attachPopup.vehicleId, id)}
                />
            )}

            {dupConfirm && (
                <DuplicateDiffDialog
                    dupConfirm={dupConfirm}
                    ocrSources={ocrSources}
                    onCancel={() => setDupConfirm(null)}
                    onConfirm={confirmUpdateExisting}
                />
            )}
        </div>
    );
}

const FIELD_LABELS = {
    plate_number: "車両番号", chassis_number: "車台番号", make: "車名", model: "型式",
    engine_model: "原動機型式", body_shape: "車体形状", vehicle_type: "種別", usage: "用途",
    private_business: "自家/事業", fuel_type: "燃料", type_designation_no: "型式指定番号",
    classification_no: "類別区分番号", owner_name: "所有者氏名", owner_address: "所有者住所",
    user_name: "使用者氏名", user_address: "使用者住所", usage_base_address: "本拠位置",
    seating_capacity: "乗車定員", max_load_kg: "最大積載量", vehicle_weight_kg: "車両重量",
    gross_weight_kg: "車両総重量", length_cm: "長さ", width_cm: "幅", height_cm: "高さ",
    front_axle_weight_kg: "前軸重", rear_axle_weight_kg: "後軸重", displacement_l: "排気量",
    first_inspection_date: "初度検査年月", inspection_expiry: "有効期間満了日", issue_date: "交付年月日",
    notes: "備考", department: "所属部署", status: "ステータス",
};

const FIELD_ORDER = [
    "plate_number","chassis_number","issue_date","first_inspection_date","inspection_expiry",
    "user_name","user_address","owner_name","owner_address","usage_base_address",
    "make","model","engine_model","vehicle_type","usage","private_business","body_shape",
    "seating_capacity","max_load_kg","vehicle_weight_kg","gross_weight_kg",
    "length_cm","width_cm","height_cm","front_axle_weight_kg","rear_axle_weight_kg",
    "displacement_l","fuel_type","type_designation_no","classification_no",
    "notes","department","status",
];

const SOURCE_LABEL = { qr: "QR", easyocr: "EasyOCR", tesseract: "Tesseract" };
const SOURCE_BADGE_CLASS = {
    qr: "text-[var(--prc-green,#00ff9c)] border-[var(--prc-green,#00ff9c)]/40 bg-[var(--prc-green,#00ff9c)]/10",
    easyocr: "text-sky-300 border-sky-400/40 bg-sky-400/10",
    tesseract: "text-amber-300 border-amber-400/40 bg-amber-400/10",
};

// Compute diff rows: existing vs new values, ordered by FIELD_ORDER.
// Each row: { field, label, oldValue, newValue, source, isNew }
//   isNew = true when existing has no value (new field on existing record)
function computeDiff(existing, payload) {
    const rows = [];
    for (const field of FIELD_ORDER) {
        const newV = payload[field];
        if (newV == null || newV === "") continue;
        const oldV = existing[field];
        const oldStr = oldV == null || oldV === "" ? "" : String(oldV);
        const newStr = String(newV);
        if (oldStr === newStr) continue;  // identical → skip (no diff)
        rows.push({
            field,
            label: FIELD_LABELS[field] || field,
            oldValue: oldStr,
            newValue: newStr,
            isNew: oldStr === "",
        });
    }
    return rows;
}

function DuplicateDiffDialog({ dupConfirm, ocrSources, onCancel, onConfirm }) {
    const { existing, payload, matchField } = dupConfirm;
    const diffRows = computeDiff(existing, payload);
    // Default: 'overwrite' for all diff rows (most users want OCR/new data applied)
    const [decisions, setDecisions] = useState(
        () => diffRows.reduce((acc, r) => ({ ...acc, [r.field]: "overwrite" }), {})
    );

    const overwriteCount = Object.values(decisions).filter(v => v === "overwrite").length;
    const allOverwrite = () => setDecisions(diffRows.reduce((acc, r) => ({ ...acc, [r.field]: "overwrite" }), {}));
    const allKeep = () => setDecisions(diffRows.reduce((acc, r) => ({ ...acc, [r.field]: "keep" }), {}));

    return (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="dup-confirm-dialog">
            <div className="prc-card p-6 max-w-3xl w-full max-h-[90vh] overflow-auto">
                <p className="prc-label-overline mb-1 !text-amber-300">// DUPLICATE DETECTED — DIFF PREVIEW</p>
                <h2 className="text-xl font-bold mb-2">同じ車両が既に登録されています</h2>
                <p className="text-sm text-[var(--prc-muted)] mb-4">
                    {matchField === "chassis_number" ? "車台番号" : "車両番号"} が一致する車両があります。<br />
                    変更がある項目を比較しています。<span className="text-[var(--prc-green)] font-semibold">項目ごとに「上書き / 既存維持」を選択</span>してから更新してください。
                </p>

                {/* Existing vehicle summary */}
                <div className="border border-[var(--prc-border)] bg-[var(--prc-surface-2)] rounded-sm p-3 mb-4 text-xs flex flex-wrap gap-x-5 gap-y-1">
                    <span><span className="text-[var(--prc-muted)] font-mono uppercase mr-1.5">既存ID</span>{existing.id}</span>
                    <span><span className="text-[var(--prc-muted)] font-mono uppercase mr-1.5">車両番号</span>{existing.plate_number}</span>
                    {existing.chassis_number && <span><span className="text-[var(--prc-muted)] font-mono uppercase mr-1.5">車台</span>{existing.chassis_number}</span>}
                    {existing.created_at && <span><span className="text-[var(--prc-muted)] font-mono uppercase mr-1.5">登録</span>{fmtYmd(existing.created_at)}</span>}
                </div>

                {diffRows.length === 0 ? (
                    <div className="border border-[var(--prc-green)]/40 bg-[var(--prc-green)]/5 rounded-sm px-4 py-6 text-center text-sm text-[var(--prc-muted)] mb-4" data-testid="dup-no-diff">
                        <p className="text-[var(--prc-green)] font-mono uppercase tracking-wider mb-1">// NO DIFF</p>
                        入力内容と既存データに差分はありません。更新の必要はありません。
                    </div>
                ) : (
                    <>
                        <div className="flex items-center justify-between mb-2 text-xs">
                            <span className="font-mono uppercase tracking-wider text-[var(--prc-muted)]">
                                差分 {diffRows.length} 項目 — 上書き選択 <span className="text-[var(--prc-green)]">{overwriteCount}</span> / {diffRows.length}
                            </span>
                            <span className="flex gap-2">
                                <button type="button" onClick={allOverwrite} className="text-[10px] font-mono uppercase tracking-wider text-[var(--prc-green)] hover:underline" data-testid="dup-all-overwrite-btn">すべて上書き</button>
                                <span className="text-[var(--prc-muted)]">|</span>
                                <button type="button" onClick={allKeep} className="text-[10px] font-mono uppercase tracking-wider text-[var(--prc-muted)] hover:underline" data-testid="dup-all-keep-btn">すべて既存維持</button>
                            </span>
                        </div>
                        <div className="border border-[var(--prc-border)] rounded-sm divide-y divide-[var(--prc-border)] mb-5">
                            {diffRows.map(row => {
                                const decision = decisions[row.field];
                                const src = ocrSources[row.field];
                                return (
                                    <div key={row.field} className="grid grid-cols-12 gap-3 items-center px-3 py-2.5 text-xs hover:bg-[var(--prc-surface-2)]/40" data-testid={`dup-diff-row-${row.field}`}>
                                        <div className="col-span-3 flex items-center gap-1.5 min-w-0">
                                            <span className="font-medium truncate" title={row.label}>{row.label}</span>
                                            {row.isNew && <span className="text-[9px] font-mono uppercase tracking-wider text-[var(--prc-green)] border border-[var(--prc-green)]/40 bg-[var(--prc-green)]/10 rounded-sm px-1 py-0 leading-none">NEW</span>}
                                            {src && (
                                                <span className={`inline-flex items-center px-1 py-0 border rounded-sm text-[9px] font-mono uppercase tracking-wider leading-none ${SOURCE_BADGE_CLASS[src]}`}>
                                                    {SOURCE_LABEL[src]}
                                                </span>
                                            )}
                                        </div>
                                        <div className="col-span-4 min-w-0">
                                            <p className="text-[10px] text-[var(--prc-muted)] font-mono uppercase">既存</p>
                                            <p className={`truncate ${decision === "keep" ? "text-[var(--prc-green)]" : "text-[var(--prc-muted)] line-through"}`} title={row.oldValue}>
                                                {row.oldValue || "—"}
                                            </p>
                                        </div>
                                        <div className="col-span-4 min-w-0">
                                            <p className="text-[10px] text-[var(--prc-muted)] font-mono uppercase">新</p>
                                            <p className={`truncate ${decision === "overwrite" ? "text-[var(--prc-green)] font-semibold" : "text-[var(--prc-muted)] line-through"}`} title={row.newValue}>
                                                {row.newValue}
                                            </p>
                                        </div>
                                        <div className="col-span-1 flex justify-end">
                                            <div className="inline-flex border border-[var(--prc-border)] rounded-sm overflow-hidden" role="radiogroup">
                                                <button
                                                    type="button"
                                                    onClick={() => setDecisions(d => ({ ...d, [row.field]: "keep" }))}
                                                    className={`px-2 py-1 text-[10px] font-mono uppercase tracking-wider transition ${decision === "keep" ? "bg-[var(--prc-muted)]/30 text-white" : "text-[var(--prc-muted)] hover:bg-[var(--prc-surface-2)]"}`}
                                                    data-testid={`dup-keep-${row.field}`}
                                                    aria-checked={decision === "keep"}
                                                    role="radio"
                                                    title="既存値を残す"
                                                >既存</button>
                                                <button
                                                    type="button"
                                                    onClick={() => setDecisions(d => ({ ...d, [row.field]: "overwrite" }))}
                                                    className={`px-2 py-1 text-[10px] font-mono uppercase tracking-wider transition ${decision === "overwrite" ? "bg-[var(--prc-green)]/20 text-[var(--prc-green)]" : "text-[var(--prc-muted)] hover:bg-[var(--prc-surface-2)]"}`}
                                                    data-testid={`dup-overwrite-${row.field}`}
                                                    aria-checked={decision === "overwrite"}
                                                    role="radio"
                                                    title="新しい値で上書き"
                                                >新値</button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                <div className="flex justify-end gap-2">
                    <button type="button" className="prc-btn-ghost" onClick={onCancel} data-testid="dup-cancel-btn">キャンセル</button>
                    <button
                        type="button"
                        className="prc-btn-primary disabled:opacity-50"
                        onClick={() => onConfirm(decisions)}
                        disabled={diffRows.length === 0 || overwriteCount === 0}
                        data-testid="dup-update-btn"
                    >
                        {overwriteCount > 0 ? `${overwriteCount}項目を上書き更新` : "更新する項目を選択してください"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * AttachmentsPopup — displays all photos/files attached to a vehicle.
 *
 * Shows 車検証 + 自賠責保険証 + any other user-uploaded attachments in a
 * grid. Tap a thumbnail to open a full-size lightbox. Admins can add new
 * files (2MB cap, auto-compressed client-side) and delete non-virtual ones.
 */
function AttachmentsPopup({ popup, canEdit, onClose, onUpload, onRemove }) {
    const inputRef = useRef(null);
    const [lightbox, setLightbox] = useState(null);

    return (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-3" onClick={onClose} data-testid="attach-popup">
            <div className="prc-card p-4 w-full sm:max-w-3xl max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 prc-btn-ghost !p-0 h-8 w-8 inline-flex items-center justify-center"
                    aria-label="閉じる"
                    data-testid="attach-popup-close"
                ><X size={16} /></button>
                <p className="prc-label-overline mb-1 !text-[var(--prc-green)]">// 添付書類</p>
                <h2 className="text-lg font-bold mb-3 pr-10 font-mono">{popup.plate} — 添付 {popup.attachments.length}件</h2>

                {popup.busy ? (
                    <p className="text-xs text-[var(--prc-muted)] py-8 text-center">読込中...</p>
                ) : popup.attachments.length === 0 ? (
                    <p className="text-xs text-[var(--prc-muted)] py-8 text-center" data-testid="attach-empty">
                        添付書類はまだありません
                    </p>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {popup.attachments.map((a) => (
                            <div key={a.id} className="relative group" data-testid={`attach-item-${a.id}`}>
                                <button
                                    type="button"
                                    onClick={() => setLightbox(a)}
                                    className="block w-full rounded-sm overflow-hidden border border-[var(--prc-border)] hover:border-[var(--prc-green)] transition"
                                >
                                    <img src={a.data_url} alt={a.name} className="w-full h-32 object-cover" />
                                </button>
                                <div className="mt-1 flex items-center justify-between">
                                    <span className="text-xs truncate flex-1 pr-1" title={a.name}>
                                        {a.virtual && <span className="text-[10px] text-[var(--prc-green)] mr-1">●</span>}
                                        {a.name}
                                    </span>
                                    {canEdit && !a.virtual && (
                                        <button
                                            type="button"
                                            onClick={() => onRemove(a.id)}
                                            className="prc-btn-ghost !p-0 h-6 w-6 inline-flex items-center justify-center text-rose-300"
                                            title="削除"
                                        ><Trash size={16} /></button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {canEdit && (
                    <div className="mt-4 pt-3 border-t border-[var(--prc-border)]">
                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) onUpload(f);
                                if (inputRef.current) inputRef.current.value = "";
                            }}
                            className="hidden"
                            data-testid="attach-popup-file-input"
                        />
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            className="prc-btn-primary inline-flex items-center gap-2 w-full sm:w-auto"
                            data-testid="attach-popup-upload"
                        >
                            <FilePlus size={16} />添付を追加 (2MB以下)
                        </button>
                    </div>
                )}
            </div>

            {lightbox && (
                <div className="fixed inset-0 bg-black/90 z-[80] flex items-center justify-center p-3" onClick={() => setLightbox(null)}>
                    <div className="relative max-w-5xl max-h-[95vh]" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            onClick={() => setLightbox(null)}
                            className="absolute top-2 right-2 prc-btn-ghost !p-0 h-9 w-9 inline-flex items-center justify-center z-10"
                            aria-label="閉じる"
                            data-testid="attach-lightbox-close"
                        ><X size={16} /></button>
                        <img src={lightbox.data_url} alt={lightbox.name} className="max-w-full max-h-[95vh] object-contain" data-testid="attach-lightbox-image" />
                    </div>
                </div>
            )}
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="mb-5">
            <p className="prc-label-overline !text-[var(--prc-green)] mb-2">// {title}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border border-[var(--prc-border)] bg-[var(--prc-surface-2)] rounded-sm p-4">
                {children}
            </div>
        </div>
    );
}

function Field({ label, cols = 1, children, field, sources, confidence }) {
    const src = field && sources ? sources[field] : null;
    const conf = field && confidence ? confidence[field] : null;
    return (
        <div className={cols === 2 ? "sm:col-span-2" : ""}>
            <label className="prc-label-overline mb-1 flex items-center gap-1.5">
                <span>{label}</span>
                {src && <OcrBadge source={src} confidence={conf} field={field} />}
            </label>
            {children}
        </div>
    );
}

/**
 * ImageUploadField — image picker + thumbnail preview for the vehicle form.
 *
 * Uses the system-wide compressImageTo1MB utility so receipts/certificates
 * are guaranteed ≤1MB before they hit the backend. Tap the thumbnail to
 * open a full-size lightbox; tap "差し替え" to pick a new file.
 */
function ImageUploadField({ label, value, onChange, cols = 1, testId }) {
    const inputRef = useRef(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const { dataUrl, bytes } = await compressImageTo1MB(file);
            onChange(dataUrl);
            toast.success(`画像を圧縮しました (${fmtKB(bytes)})`);
        } catch (err) {
            toast.error(err.message || "画像処理に失敗しました");
        } finally {
            // Allow re-uploading the same file
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <div className={cols === 2 ? "sm:col-span-2" : ""}>
            <label className="prc-label-overline mb-1 flex items-center gap-1.5">
                <span>{label}</span>
            </label>
            <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid={testId ? `${testId}-input` : undefined} />
            {value ? (
                <div className="flex items-start gap-3">
                    <button
                        type="button"
                        onClick={() => setLightboxOpen(true)}
                        className="flex-shrink-0 rounded-sm border border-[var(--prc-border)] hover:border-[var(--prc-green)] transition overflow-hidden"
                        data-testid={testId ? `${testId}-thumb` : undefined}
                    >
                        <img src={value} alt={label} className="h-24 w-32 object-cover" />
                    </button>
                    <div className="flex flex-col gap-1.5">
                        <button type="button" onClick={() => inputRef.current?.click()} className="prc-btn-ghost inline-flex items-center gap-2 text-xs">
                            <Camera size={16} />差し替え
                        </button>
                        <button type="button" onClick={() => onChange("")} className="prc-btn-ghost inline-flex items-center gap-2 text-xs !text-rose-300">
                            <X size={16} />削除
                        </button>
                    </div>
                </div>
            ) : (
                <button type="button" onClick={() => inputRef.current?.click()} className="prc-btn-ghost inline-flex items-center gap-2 text-xs" data-testid={testId ? `${testId}-add` : undefined}>
                    <Camera size={16} />写真を撮影 / アップロード
                </button>
            )}
            {lightboxOpen && value && (
                <div className="fixed inset-0 bg-black/85 z-[70] flex items-center justify-center p-3" onClick={() => setLightboxOpen(false)}>
                    <div className="relative max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setLightboxOpen(false)} className="absolute top-2 right-2 prc-btn-ghost !px-2 !py-1 z-10"><X size={16} /></button>
                        <img src={value} alt={label} className="max-h-[90vh] max-w-full object-contain" />
                    </div>
                </div>
            )}
        </div>
    );
}

const SOURCE_META = {
    qr: { label: "QR", className: "text-[var(--prc-green,#00ff9c)] border-[var(--prc-green,#00ff9c)]/40 bg-[var(--prc-green,#00ff9c)]/10",
        title: "MLIT 二次元バーコード由来 — 国交省規格の構造化データで100%正確" },
    easyocr: { label: "EasyOCR", className: "text-sky-300 border-sky-400/40 bg-sky-400/10",
        title: "EasyOCR(機械学習)由来 — 諸元表の文字を画像認識で抽出。値の桁数を念のため目視確認" },
    tesseract: { label: "Tesseract", className: "text-amber-300 border-amber-400/40 bg-amber-400/10",
        title: "Tesseract(従来型OCR)由来 — フォーム文字の単純パターンマッチ。誤読の可能性が比較的高めなので必ず目視確認" },
};

const CONF_DOT = { high: "bg-emerald-400", medium: "bg-yellow-400", low: "bg-rose-400" };

function OcrBadge({ source, confidence, field }) {
    const meta = SOURCE_META[source];
    if (!meta) return null;
    const dotColor = CONF_DOT[confidence] || "bg-zinc-400";
    const tip = `${meta.title}（信頼度: ${confidence || "不明"}）`;
    return (
        <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 border rounded-sm text-[9px] font-mono uppercase tracking-wider leading-none ${meta.className}`}
            title={tip}
            data-testid={`ocr-badge-${field}`}
        >
            <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} aria-hidden />
            {meta.label}
        </span>
    );
}

// Format YYYY年MM月DD日 HH:MM (JST)
function _formatTs(iso) {
    return fmtYmdTime(iso, "—");
}

const SOURCE_PILL = {
    manual: { label: "手動更新", className: "border-zinc-500/40 bg-zinc-500/10 text-zinc-200" },
    ocr_diff: { label: "OCR差分更新", className: "border-sky-400/40 bg-sky-400/10 text-sky-200" },
};

function VehicleHistoryView({ vehicleId, onClose }) {
    const [history, setHistory] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setHistory(null);
        setError(null);
        api.get(`/vehicles/${vehicleId}/history`)
            .then(({ data }) => { if (!cancelled) setHistory(data); })
            .catch((e) => { if (!cancelled) setError(formatApiError(e.response?.data?.detail) || "履歴を取得できませんでした"); });
        return () => { cancelled = true; };
    }, [vehicleId]);

    if (error) {
        return (
            <div className="border border-rose-500/40 bg-rose-500/10 rounded-sm p-4 text-sm text-rose-200" data-testid="history-error">
                {error}
            </div>
        );
    }
    if (history === null) {
        return (
            <div className="text-center py-12 text-xs font-mono uppercase tracking-wider text-[var(--prc-muted)] animate-pulse" data-testid="history-loading">
                // LOADING HISTORY...
            </div>
        );
    }
    if (history.length === 0) {
        return (
            <div className="border border-[var(--prc-border)] bg-[var(--prc-surface-2)] rounded-sm px-4 py-12 text-center text-sm text-[var(--prc-muted)]" data-testid="history-empty">
                <p className="text-[var(--prc-green)] font-mono uppercase tracking-wider mb-2">// NO HISTORY</p>
                この車両にはまだ変更履歴がありません。<br />
                車両情報を更新すると、ここに変更内容が時系列で記録されます。
                <div className="flex justify-end mt-6">
                    <button type="button" className="prc-btn-ghost" onClick={onClose} data-testid="history-close-btn">閉じる</button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="history-view">
            <p className="text-xs text-[var(--prc-muted)] font-mono uppercase tracking-wider mb-3">
                // {history.length} 件の変更を表示（新しい順）
            </p>
            <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
                {history.map((h) => {
                    const pill = SOURCE_PILL[h.source] || SOURCE_PILL.manual;
                    return (
                        <div key={h.id} className="border border-[var(--prc-border)] bg-[var(--prc-surface-2)] rounded-sm p-3" data-testid={`history-entry-${h.id}`}>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2.5 text-xs">
                                <span className="font-mono uppercase tracking-wider text-[var(--prc-green)]">
                                    {_formatTs(h.changed_at)}
                                </span>
                                <span className={`inline-flex items-center px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-wider leading-none ${pill.className}`}>
                                    {pill.label}
                                </span>
                                {h.changed_by_name && (
                                    <span className="text-[var(--prc-muted)]">by <span className="text-white">{h.changed_by_name}</span></span>
                                )}
                                <span className="text-[var(--prc-muted)] ml-auto">{h.changes.length} 項目変更</span>
                            </div>
                            <div className="space-y-1">
                                {h.changes.map((c, idx) => (
                                    <div key={idx} className="grid grid-cols-12 gap-2 items-baseline text-xs border-l-2 border-[var(--prc-green)]/30 pl-2.5 py-0.5">
                                        <span className="col-span-3 font-medium truncate" title={FIELD_LABELS[c.field] || c.field}>
                                            {FIELD_LABELS[c.field] || c.field}
                                        </span>
                                        <span className="col-span-4 text-[var(--prc-muted)] line-through truncate" title={c.old_value == null ? "" : String(c.old_value)}>
                                            {c.old_value == null || c.old_value === "" ? "—" : String(c.old_value)}
                                        </span>
                                        <span className="col-span-1 text-center text-[var(--prc-muted)]">→</span>
                                        <span className="col-span-4 text-[var(--prc-green)] font-semibold truncate" title={c.new_value == null ? "" : String(c.new_value)}>
                                            {c.new_value == null || c.new_value === "" ? "—" : String(c.new_value)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="flex justify-end mt-5 pt-4 border-t border-[var(--prc-border)]">
                <button type="button" className="prc-btn-ghost" onClick={onClose} data-testid="history-close-btn">閉じる</button>
            </div>
        </div>
    );
}

// Lifeline type → visual config. Each entry: color/bg, icon char, label
const LIFELINE_TYPE = {
    history:     { pill: "text-zinc-200 border-zinc-500/40 bg-zinc-500/10",      label: "仕様変更", dot: "bg-zinc-400" },
    maintenance: { pill: "text-amber-300 border-amber-400/40 bg-amber-400/10",   label: "整備",     dot: "bg-amber-400" },
    operation:   { pill: "text-[var(--prc-green,#00ff9c)] border-[var(--prc-green,#00ff9c)]/40 bg-[var(--prc-green,#00ff9c)]/10", label: "運行", dot: "bg-[var(--prc-green,#00ff9c)]" },
    inspection:  { pill: "text-sky-300 border-sky-400/40 bg-sky-400/10",         label: "点検",     dot: "bg-sky-400" },
};

function _formatLifelineDate(raw) {
    if (!raw) return "—";
    // Pure date strings render as 年月日, full ISO timestamps include HH:MM.
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return fmtYmd(raw, raw);
    return fmtYmdTime(raw, raw);
}

function VehicleLifelineView({ vehicleId, onClose }) {
    const [entries, setEntries] = useState(null);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        let cancelled = false;
        setEntries(null);
        setError(null);
        api.get(`/vehicles/${vehicleId}/lifeline`)
            .then(({ data }) => { if (!cancelled) setEntries(data); })
            .catch((e) => { if (!cancelled) setError(formatApiError(e.response?.data?.detail) || "ライフラインを取得できませんでした"); });
        return () => { cancelled = true; };
    }, [vehicleId]);

    const filtered = (entries || []).filter((e) => filter === "all" || e.type === filter);
    const counts = (entries || []).reduce((a, e) => ({ ...a, [e.type]: (a[e.type] || 0) + 1 }), {});

    if (error) return <div className="border border-rose-500/40 bg-rose-500/10 rounded-sm p-4 text-sm text-rose-200" data-testid="lifeline-error">{error}</div>;
    if (entries === null) return <div className="text-center py-12 text-xs font-mono uppercase tracking-wider text-[var(--prc-muted)] animate-pulse" data-testid="lifeline-loading">// LOADING LIFELINE...</div>;
    if (entries.length === 0) {
        return (
            <div className="border border-[var(--prc-border)] bg-[var(--prc-surface-2)] rounded-sm px-4 py-12 text-center text-sm text-[var(--prc-muted)]" data-testid="lifeline-empty">
                <p className="text-[var(--prc-green)] font-mono uppercase tracking-wider mb-2">// NO EVENTS YET</p>
                この車両にはまだイベントがありません。<br />
                運行記録・整備記録・点検・車両情報変更がここに時系列で集約されます。
                <div className="flex justify-end mt-6"><button type="button" className="prc-btn-ghost" onClick={onClose} data-testid="lifeline-close-btn">閉じる</button></div>
            </div>
        );
    }

    const FilterBtn = ({ v, label, count }) => (
        <button
            type="button"
            onClick={() => setFilter(v)}
            className={`px-2.5 py-1 rounded-sm border text-[10px] font-mono uppercase tracking-wider transition ${
                filter === v
                    ? "border-[var(--prc-green)] bg-[var(--prc-green)]/10 text-[var(--prc-green)]"
                    : "border-[var(--prc-border)] text-[var(--prc-muted)] hover:text-white"
            }`}
            data-testid={`lifeline-filter-${v}`}
        >{label} {count != null && <span className="opacity-70">{count}</span>}</button>
    );

    return (
        <div data-testid="lifeline-view">
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <FilterBtn v="all" label="全て" count={entries.length} />
                <FilterBtn v="operation" label={LIFELINE_TYPE.operation.label} count={counts.operation || 0} />
                <FilterBtn v="maintenance" label={LIFELINE_TYPE.maintenance.label} count={counts.maintenance || 0} />
                <FilterBtn v="inspection" label={LIFELINE_TYPE.inspection.label} count={counts.inspection || 0} />
                <FilterBtn v="history" label={LIFELINE_TYPE.history.label} count={counts.history || 0} />
            </div>
            <div className="relative max-h-[55vh] overflow-y-auto pr-1" data-testid="lifeline-list">
                <div className="absolute left-[6px] top-1 bottom-1 w-px bg-[var(--prc-border)]" aria-hidden />
                <div className="space-y-3">
                    {filtered.length === 0 && (
                        <p className="text-xs text-[var(--prc-muted)] py-8 text-center font-mono uppercase tracking-wider">該当するイベントがありません</p>
                    )}
                    {filtered.map((e) => {
                        const cfg = LIFELINE_TYPE[e.type] || LIFELINE_TYPE.history;
                        return (
                            <div key={e.id} className="relative pl-6" data-testid={`lifeline-entry-${e.id}`}>
                                <span className={`absolute left-0 top-1.5 w-3 h-3 rounded-full ring-2 ring-[var(--prc-surface)] ${cfg.dot}`} aria-hidden />
                                <div className="border border-[var(--prc-border)] bg-[var(--prc-surface-2)] rounded-sm p-3">
                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1.5">
                                        <span className={`inline-block px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-wider ${cfg.pill}`}>{cfg.label}</span>
                                        <span className="text-xs font-mono text-[var(--prc-muted)]">{_formatLifelineDate(e.at)}</span>
                                        {e.actor && <span className="text-xs text-[var(--prc-muted)]">by <span className="text-white">{e.actor}</span></span>}
                                    </div>
                                    <p className="text-sm font-medium">{e.title}</p>
                                    {e.description && <p className="text-xs text-[var(--prc-muted)] mt-0.5 whitespace-pre-wrap">{e.description}</p>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="flex justify-end mt-5 pt-4 border-t border-[var(--prc-border)]">
                <button type="button" className="prc-btn-ghost" onClick={onClose} data-testid="lifeline-close-btn">閉じる</button>
            </div>
        </div>
    );
}


// ============ Mileage-based maintenance forecast view ============

const MILEAGE_STATUS = {
    overdue: { pill: "text-rose-300 border-rose-400/40 bg-rose-500/10", bar: "bg-rose-500", label: "超過" },
    warning: { pill: "text-amber-300 border-amber-400/40 bg-amber-500/10", bar: "bg-amber-400", label: "接近" },
    ok:      { pill: "text-[var(--prc-green,#00ff9c)] border-[var(--prc-green,#00ff9c)]/40 bg-[var(--prc-green,#00ff9c)]/10", bar: "bg-[var(--prc-green,#00ff9c)]", label: "順調" },
};

const MILEAGE_KIND_OPTIONS = [
    { v: "inspection", l: "使用前点検" },
    { v: "oil_change", l: "オイル交換" },
    { v: "tire_rotation", l: "タイヤローテーション" },
    { v: "tire_change", l: "タイヤ交換" },
    { v: "shaken", l: "車検整備" },
    { v: "fuel_check", l: "給油・燃費確認" },
    { v: "custom", l: "カスタム" },
];

function VehicleMileageForecastView({ vehicleId, onClose }) {
    const { can } = useAuth();
    const canManage = can("mileage_rules.manage");
    const [forecasts, setForecasts] = useState(null);
    const [rules, setRules] = useState([]);
    const [error, setError] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [newRule, setNewRule] = useState({
        label: "", kind: "oil_change", interval_km: "", scope: "vehicle", baseline_km: "",
    });

    const refresh = async () => {
        try {
            setError(null);
            const [fc, rl] = await Promise.all([
                api.get(`/mileage-rules/forecast/${vehicleId}`),
                api.get(`/mileage-rules?vehicle_id=${vehicleId}`),
            ]);
            setForecasts(fc.data);
            setRules(rl.data);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "走行距離アラートの取得に失敗しました");
        }
    };
    useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [vehicleId]);

    const addRule = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        const payload = {
            label: newRule.label.trim(),
            kind: newRule.kind,
            interval_km: parseInt(newRule.interval_km),
            vehicle_id: newRule.scope === "vehicle" ? vehicleId : null,
            baseline_km: newRule.baseline_km === "" ? null : parseInt(newRule.baseline_km),
        };
        if (!payload.label || !Number.isFinite(payload.interval_km) || payload.interval_km <= 0) {
            toast.error("ラベルと周期 (km) を正しく入力してください");
            return;
        }
        try {
            await api.post("/mileage-rules", payload);
            toast.success("ルールを追加しました");
            setShowCreate(false);
            setNewRule({ label: "", kind: "oil_change", interval_km: "", scope: "vehicle", baseline_km: "" });
            await refresh();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "登録失敗");
        }
    };

    const removeRule = async (rid) => {
        if (!window.confirm("このルールを削除しますか？")) return;
        try {
            await api.delete(`/mileage-rules/${rid}`);
            toast.success("削除しました");
            await refresh();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "削除失敗");
        }
    };

    if (error) return <div className="border border-rose-500/40 bg-rose-500/10 rounded-sm p-4 text-sm text-rose-200" data-testid="mileage-error">{error}</div>;
    if (forecasts === null) return <div className="text-center py-12 text-xs font-mono uppercase tracking-wider text-[var(--prc-muted)] animate-pulse" data-testid="mileage-loading">// LOADING...</div>;

    const overdueCount = forecasts.filter(f => f.status === "overdue").length;
    const warningCount = forecasts.filter(f => f.status === "warning").length;

    return (
        <div data-testid="mileage-view">
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <p className="text-xs font-mono uppercase tracking-wider text-[var(--prc-muted)]">
                    // {forecasts.length} 件のルール
                </p>
                {overdueCount > 0 && (
                    <span className="inline-block px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-wider border-rose-400/40 bg-rose-500/10 text-rose-300" data-testid="mileage-overdue-count">
                        超過 {overdueCount}
                    </span>
                )}
                {warningCount > 0 && (
                    <span className="inline-block px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-wider border-amber-400/40 bg-amber-500/10 text-amber-300">
                        接近 {warningCount}
                    </span>
                )}
                <div className="ml-auto flex gap-2">
                    {canManage && (
                        <button
                            type="button"
                            onClick={() => setShowCreate((v) => !v)}
                            className="prc-btn-ghost !px-2 !py-1 inline-flex items-center gap-1 text-xs"
                            data-testid="mileage-add-btn"
                        >{showCreate ? "キャンセル" : "+ ルール追加"}</button>
                    )}
                </div>
            </div>

            {showCreate && canManage && (
                <div className="border border-[var(--prc-border)] bg-[var(--prc-surface-2)] rounded-sm p-3 mb-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs" data-testid="mileage-add-form">
                    <div className="sm:col-span-3">
                        <label className="prc-label-overline block mb-1">ラベル *</label>
                        <input className="prc-input" value={newRule.label} onChange={(e) => setNewRule({ ...newRule, label: e.target.value })} placeholder="例: オイル交換" data-testid="mileage-form-label" />
                    </div>
                    <div>
                        <label className="prc-label-overline block mb-1">種別</label>
                        <select className="prc-input" value={newRule.kind} onChange={(e) => setNewRule({ ...newRule, kind: e.target.value })} data-testid="mileage-form-kind">
                            {MILEAGE_KIND_OPTIONS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="prc-label-overline block mb-1">周期 (km) *</label>
                        <input type="number" min="1" className="prc-input" value={newRule.interval_km} onChange={(e) => setNewRule({ ...newRule, interval_km: e.target.value })} placeholder="5000" data-testid="mileage-form-interval" />
                    </div>
                    <div>
                        <label className="prc-label-overline block mb-1">適用範囲</label>
                        <select className="prc-input" value={newRule.scope} onChange={(e) => setNewRule({ ...newRule, scope: e.target.value })} data-testid="mileage-form-scope">
                            <option value="vehicle">この車両のみ</option>
                            <option value="company">全車両共通</option>
                        </select>
                    </div>
                    <div className="sm:col-span-3">
                        <label className="prc-label-overline block mb-1">起算オドメータ (km)（任意）</label>
                        <input type="number" min="0" className="prc-input" value={newRule.baseline_km} onChange={(e) => setNewRule({ ...newRule, baseline_km: e.target.value })} placeholder="空欄なら現在オドメータの直前しきい値を自動計算" data-testid="mileage-form-baseline" />
                    </div>
                    <div className="sm:col-span-3 flex justify-end">
                        <button type="button" onClick={addRule} className="prc-btn-primary !px-3 !py-1 text-xs" data-testid="mileage-form-submit">ルールを追加</button>
                    </div>
                </div>
            )}

            {forecasts.length === 0 ? (
                <div className="border border-[var(--prc-border)] bg-[var(--prc-surface-2)] rounded-sm px-4 py-12 text-center text-sm text-[var(--prc-muted)]" data-testid="mileage-empty">
                    <p className="text-[var(--prc-green)] font-mono uppercase tracking-wider mb-2">// NO RULES</p>
                    整備計画ルールがまだ登録されていません。<br />
                    {canManage ? "「+ ルール追加」から 走行距離ベースの自動通知を設定できます。" : "管理者にルールの追加を依頼してください。"}
                </div>
            ) : (
                <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                    {forecasts.map((f) => {
                        const cfg = MILEAGE_STATUS[f.status] || MILEAGE_STATUS.ok;
                        const rule = rules.find((r) => r.id === f.rule_id);
                        return (
                            <div key={f.rule_id} className="border border-[var(--prc-border)] bg-[var(--prc-surface-2)] rounded-sm p-3" data-testid={`mileage-rule-${f.rule_id}`}>
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2">
                                    <span className={`inline-block px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-wider ${cfg.pill}`}>{cfg.label}</span>
                                    <p className="text-sm font-medium">{f.label}</p>
                                    <span className="text-[10px] font-mono text-[var(--prc-muted)] uppercase">
                                        {f.scope === "company" ? "全車両" : "車両別"} · {f.kind_label}
                                    </span>
                                    <span className="ml-auto text-xs font-mono text-[var(--prc-muted)]">
                                        {f.remaining_km >= 0
                                            ? <>あと <span className="text-white">{f.remaining_km.toLocaleString()}</span> km</>
                                            : <>超過 <span className="text-rose-300">{Math.abs(f.remaining_km).toLocaleString()}</span> km</>}
                                    </span>
                                    {canManage && rule?.vehicle_id && (
                                        <button
                                            type="button"
                                            onClick={() => removeRule(f.rule_id)}
                                            className="text-[10px] font-mono uppercase text-rose-300 hover:underline ml-1"
                                            data-testid={`mileage-delete-${f.rule_id}`}
                                            title="この車両ルールを削除"
                                        >削除</button>
                                    )}
                                </div>
                                <div className="h-1.5 bg-[var(--prc-border)] rounded-sm overflow-hidden mb-1.5">
                                    <div className={`h-full ${cfg.bar} transition-all`} style={{ width: `${Math.min(f.progress_pct, 100)}%` }} aria-hidden />
                                </div>
                                <div className="flex justify-between text-[10px] font-mono text-[var(--prc-muted)]">
                                    <span>前回 {f.last_event_km.toLocaleString()} km</span>
                                    <span className={f.status === "overdue" ? "text-rose-300" : ""}>
                                        現在 <span className="text-white">{f.current_km.toLocaleString()} km</span>
                                    </span>
                                    <span>次回 {f.due_at_km.toLocaleString()} km ({f.interval_km.toLocaleString()} km周期)</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="flex justify-end mt-5 pt-4 border-t border-[var(--prc-border)]">
                <button type="button" className="prc-btn-ghost" onClick={onClose} data-testid="mileage-close-btn">閉じる</button>
            </div>
        </div>
    );
}

